import { ReactNode } from 'react';
import { Box, Flex } from '@chakra-ui/react';

import { accent, font, neutral, primaryAction, radius, text } from 'styles/revampTokens';

export interface TasksViewer {
  name: string;
  initials: string;
  workspace: string;
  userId?: number;
}

export const viewerFromUser = (
  user?: { id?: number; firstname?: string; lastname?: string; company?: string } | null,
): TasksViewer | undefined => {
  if (!user) return undefined;
  const first = user.firstname?.trim() ?? '';
  const last = user.lastname?.trim() ?? '';
  const name = [first, last].filter(Boolean).join(' ');
  const initials = `${first.slice(0, 1)}${last.slice(0, 1)}`.toUpperCase();
  if (!name && !initials && !user.company) return undefined;
  return {
    name: name || 'Signed in',
    initials: initials || 'B',
    workspace: user.company?.trim() || 'Planning',
    userId: user.id,
  };
};

/**
 * Workflow steps, styled like the model page tabs.
 * The number is what makes the row read as a sequence. The first step stays
 * selected until the address names another step that exists.
 */
export const StepTabs = ({
  tabs,
  activeId,
  onSelect,
}: {
  tabs: Array<{ id: string; label: string }>;
  activeId?: string;
  onSelect: (id: string) => void;
}) => {
  const known = tabs.some((item) => item.id === activeId);
  return (
    <Flex
      as='nav'
      role='tablist'
      aria-label='Planning activities'
      gap='4px'
      overflowX='auto'
      flex='1'
      minW={0}
      align='center'
    >
      {tabs.map((item, index) => {
        const active = known ? item.id === activeId : index === 0;
        const step = String(index + 1);
        return (
          <Flex
            key={item.id}
            as='button'
            type='button'
            role='tab'
            aria-selected={active}
            aria-label={`${step} ${item.label}`}
            onClick={() => onSelect(item.id)}
            align='center'
            h='32px'
            px='12px'
            py='6px'
            flexShrink={0}
            borderRadius={radius.control}
            bg={active ? accent.solid : 'transparent'}
            color={active ? 'white' : primaryAction.bg}
            fontSize='12px'
            fontWeight={500}
            lineHeight='1'
            whiteSpace='nowrap'
            _hover={{ bg: active ? accent.solid : 'gray.100' }}
          >
            <Box
              as='span'
              mr='8px'
              w='18px'
              h='18px'
              borderRadius='full'
              display='inline-flex'
              alignItems='center'
              justifyContent='center'
              flexShrink={0}
              fontSize='11px'
              fontWeight={600}
              bg={active ? 'white' : 'transparent'}
              color={active ? accent.solid : primaryAction.bg}
              border={active ? 'none' : '1px solid #D1D6DD'}
            >
              {step}
            </Box>
            {item.label}
          </Flex>
        );
      })}
    </Flex>
  );
};

export const PlanningPage = ({
  children,
  centered = false,
}: {
  children: ReactNode;
  centered?: boolean;
}) => (
  <Box
    h='calc(100vh - var(--blox-header-height, 53px))'
    bg={neutral.canvas}
    display='flex'
    flexDirection='column'
    overflow='hidden'
  >
    <Box
      as='main'
      bg={neutral.surface}
      flex='1'
      display='flex'
      flexDirection='column'
      minH={0}
      overflow='hidden'
      px={{ base: '19px', md: '32px' }}
      py='16px'
    >
      <Box
        flex='1'
        display='flex'
        flexDirection='column'
        minH={0}
        w='100%'
        maxW={centered ? '960px' : undefined}
        mx={centered ? 'auto' : undefined}
      >
        {children}
      </Box>
    </Box>
  </Box>
);

export const pageTitle = {
  as: 'h1' as const,
  fontFamily: font.display,
  fontSize: '22px',
  lineHeight: '1.2',
  fontWeight: 600,
  color: text.primary,
};
